import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface Item {
  title: string;
  year?: string;
  poster?: string | null;
}

/**
 * Cinema lobby marquee: scrolling poster strip with bulb header.
 */
const CACHE_KEY = "swiftix_now_playing_marquee_v2";
const CACHE_TTL_MS = 30 * 60 * 1000;

export const NowPlayingMarquee = () => {
  const [items, setItems] = useState<Item[]>([]);

  useEffect(() => {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) {
      try {
        const { ts, items: cachedItems } = JSON.parse(cached);
        if (Date.now() - ts < CACHE_TTL_MS && Array.isArray(cachedItems)) {
          setItems(cachedItems);
          return;
        }
      } catch { /* ignore */ }
    }

    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.functions.invoke("tmdb-discover", {
          body: { sortBy: "popularity.desc", page: 1, mediaType: "movie" },
        });
        const list: Item[] =
          (data?.results || [])
            .filter((r: { title?: string; poster_path?: string | null }) => r.title && r.poster_path)
            .slice(0, 16)
            .map((r: { title: string; release_date?: string; poster_path?: string | null }) => ({
              title: r.title,
              year: r.release_date?.slice(0, 4),
              poster: r.poster_path ? `https://image.tmdb.org/t/p/w185${r.poster_path}` : null,
            }));
        if (!cancelled && list.length) {
          setItems(list);
          sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), items: list }));
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!items.length) return null;

  const row = (
    <div className="flex gap-4 items-center px-4 shrink-0">
      {items.map((it, i) => (
        <div
          key={i}
          className="vintage-marquee-tile group"
          title={it.title}
        >
          {it.poster ? (
            <img src={it.poster} alt={it.title} loading="lazy" className="h-full w-full object-cover" />
          ) : (
            <div className="h-full w-full bg-[#1a0e0a]" />
          )}
          <div className="vintage-marquee-caption">
            <div className="text-[9px] uppercase tracking-[0.3em] text-amber-300/80 font-bold truncate">
              {it.title}
            </div>
            {it.year && (
              <div className="text-[8px] text-amber-200/50">{it.year}</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="relative overflow-hidden border-y border-amber-300/15 bg-gradient-to-b from-black/80 via-[#1a0808]/85 to-black/80 py-3" role="marquee" aria-label="Now showing">
      {/* Top bulb strip */}
      <div className="marquee-bulb-strip absolute top-0 inset-x-0 h-1.5 flex items-center justify-around px-2 pointer-events-none">
        {Array.from({ length: 40 }).map((_, i) => (
          <span
            key={i}
            className="h-1 w-1 rounded-full bg-amber-300"
            style={{ boxShadow: '0 0 6px 1px rgba(252,211,77,0.7)' }}
          />
        ))}
      </div>

      <div className="flex marquee-track">
        {row}
        {row}
      </div>
      <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-background to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-background to-transparent" />
    </div>
  );
};
